<template>
  <aside class="sidebar" :class="{ collapsed: isCollapsed }">
    <div class="sidebar-header">
      <router-link to="/" class="sidebar-brand">
        <img src="/icon.png" alt="LES PRÉFABRIQUÉS DU FASO Logo" class="sidebar-logo" />
        <span class="sidebar-title">LES PRÉFABRIQUÉS <br/> DU FASO</span>
      </router-link>
    </div>
    <nav class="sidebar-nav">
      <ul class="nav-list">
        <li v-for="item in navItems" :key="item.path" class="nav-item">
          <router-link
            :to="item.path"
            class="nav-link"
            :class="{ active: $route.path === item.path }"
          >
            <span class="nav-icon">{{ item.icon }}</span>
            <span class="nav-label">{{ item.label }}</span>
          </router-link>
        </li>
      </ul>
    </nav>
    
    <div v-if="user" class="sidebar-footer">
      <div class="sidebar-user">
        <div class="sidebar-user-header">
          <button
            class="sidebar-user-settings-btn"
            @click="showChangePasswordModal = true"
            title="Paramètres (changer le mot de passe)"
            aria-label="Paramètres (changer le mot de passe)"
          >
            ⚙️
          </button>
          <div class="sidebar-user-info">
            <div class="sidebar-user-name">{{ user.nom }}</div>
            <div class="sidebar-user-email">{{ user.email }}</div>
          </div>
        </div>
        <button class="sidebar-logout-btn" @click="handleLogout" title="Se déconnecter">
          <span class="sidebar-logout-icon" aria-hidden="true">⎋</span>
          <span class="sidebar-logout-label">Déconnexion</span>
        </button>
      </div>
    </div>
    
    <button class="sidebar-toggle" @click="toggleCollapse" :title="isCollapsed ? 'Agrandir' : 'Réduire'">
      {{ isCollapsed ? '→' : '←' }}
    </button>

    <!-- Modal changement de mot de passe -->
    <Modal v-if="showChangePasswordModal" @close="closePasswordModal">
      <template #title>Changer le mot de passe</template>
      <form @submit.prevent="handleChangePassword" class="change-password-form">
        <BaseInput
          v-model.trim="passwordForm.currentPassword"
          label="Mot de passe actuel"
          type="password"
          placeholder="••••••••"
          required
        />
        <BaseInput
          v-model.trim="passwordForm.newPassword"
          label="Nouveau mot de passe"
          type="password"
          placeholder="••••••••"
          required
        />
        <BaseInput
          v-model.trim="passwordForm.confirmPassword"
          label="Confirmer le nouveau mot de passe"
          type="password"
          placeholder="••••••••"
          required
        />
        <div v-if="passwordError" class="error-message">{{ passwordError }}</div>
        <div v-if="passwordSuccess" class="success-message">{{ passwordSuccess }}</div>
        <div class="form-actions">
          <BaseButton type="button" variant="ghost" @click="closePasswordModal">Annuler</BaseButton>
          <BaseButton type="submit" variant="primary" :loading="changingPassword">
            Enregistrer
          </BaseButton>
        </div>
      </form>
    </Modal>
  </aside>
</template>

<script setup lang="ts">
import { ref, computed, reactive } from 'vue';
import { RouterLink, useRouter } from 'vue-router';
import { useAuth } from '../composables/useAuth';
import Modal from './Modal.vue';
import BaseInput from './BaseInput.vue';
import BaseButton from './BaseButton.vue';
import { apiFetch } from '../utils/api';

const { user, logout } = useAuth();
const router = useRouter();
const isCollapsed = ref(false);
const showChangePasswordModal = ref(false);
const changingPassword = ref(false);
const passwordError = ref<string | null>(null);
const passwordSuccess = ref<string | null>(null);

const passwordForm = reactive({
  currentPassword: '',
  newPassword: '',
  confirmPassword: ''
});

function toggleCollapse() {
  isCollapsed.value = !isCollapsed.value;
}

async function handleLogout() {
  await logout();
  router.push({ name: 'login' });
}

function closePasswordModal() {
  showChangePasswordModal.value = false;
  passwordForm.currentPassword = '';
  passwordForm.newPassword = '';
  passwordForm.confirmPassword = '';
  passwordError.value = null;
  passwordSuccess.value = null;
}

async function handleChangePassword() {
  passwordError.value = null;
  passwordSuccess.value = null;

  if (passwordForm.newPassword !== passwordForm.confirmPassword) {
    passwordError.value = 'Les mots de passe ne correspondent pas';
    return;
  }

  if (passwordForm.newPassword.length < 8) {
    passwordError.value = 'Le mot de passe doit contenir au moins 8 caractères';
    return;
  }

  changingPassword.value = true;
  try {
    await apiFetch('/authentification/change-password', {
      method: 'POST',
      body: {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword
      }
    });
    passwordSuccess.value = 'Mot de passe modifié avec succès';
    setTimeout(() => {
      closePasswordModal();
    }, 1500);
  } catch (err) {
    passwordError.value = err instanceof Error ? err.message : 'Erreur lors du changement de mot de passe';
  } finally {
    changingPassword.value = false;
  }
}

// Items de navigation selon le rôle
const navItems = computed(() => {
  const role = user.value?.role || '';
  const allItems = [
    { path: '/bureau', label: 'Bureau', icon: '🏢', roles: ['ADMIN', 'BUREAU', 'OFFICE'] },
    { path: '/usine', label: 'Usine', icon: '🏭', roles: ['ADMIN', 'USINE', 'FACTORY'] },
    { path: '/logistique', label: 'Logistique', icon: '🚚', roles: ['ADMIN', 'LOGISTIQUE'] },
    { path: '/site-chantier', label: 'Site / Chantier', icon: '🛠️', roles: ['ADMIN', 'CHANTIER', 'SUPERVISION', 'SITE'] },
    { path: '/users', label: 'Utilisateurs', icon: '👥', roles: ['ADMIN'] }
  ];

  // Filtrer selon le rôle (ADMIN voit tout)
  if (role === 'ADMIN') {
    return allItems;
  }

  return allItems.filter(item => item.roles.includes(role));
});
</script>

<style scoped>
.sidebar {
  position: fixed;
  left: 0;
  top: 0;
  bottom: 0;
  width: 240px;
  background: var(--color-bg-primary);
  border-right: 1px solid var(--color-border-light);
  display: flex;
  flex-direction: column;
  transition: width 0.3s ease;
  z-index: 100;
  box-shadow: var(--shadow-sm);
}

.sidebar.collapsed {
  width: 60px;
}

.sidebar-header {
  padding: var(--spacing-lg);
  border-bottom: 1px solid var(--color-border-light);
  display: flex;
  align-items: center;
  justify-content: center;
}

.sidebar-brand {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  text-decoration: none;
  transition: opacity var(--transition-base);
}

.sidebar-brand:hover {
  opacity: 0.8;
}

.sidebar-logo {
  width: 40px;
  height: 40px;
  object-fit: contain;
  flex-shrink: 0;
}

.sidebar-title {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-primary);
  white-space: nowrap;
  transition: opacity var(--transition-base);
}

.sidebar.collapsed .sidebar-title {
  opacity: 0;
  width: 0;
  overflow: hidden;
}

.sidebar-nav {
  flex: 1;
  overflow-y: auto;
  padding: var(--spacing-md) 0;
}

.nav-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.nav-item {
  margin: 0;
}

.nav-link {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  padding: var(--spacing-md) var(--spacing-lg);
  color: var(--color-text-secondary);
  text-decoration: none;
  transition: all var(--transition-base);
  border-left: 3px solid transparent;
}

.nav-link:hover {
  background: var(--color-bg-secondary);
  color: var(--color-text-primary);
}

.nav-link.active {
  background: var(--color-bg-secondary);
  color: var(--color-primary);
  border-left-color: var(--color-primary);
  font-weight: var(--font-weight-semibold);
}

.nav-icon {
  font-size: var(--font-size-lg);
  width: 24px;
  text-align: center;
  flex-shrink: 0;
}

.nav-label {
  flex: 1;
  font-size: var(--font-size-md);
}

.sidebar.collapsed .nav-label {
  display: none;
}

.sidebar-footer {
  padding: var(--spacing-md);
  border-top: 1px solid var(--color-border-light);
  background: var(--color-bg-secondary);
}

.sidebar-user {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.sidebar-user-header {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding-bottom: var(--spacing-sm);
  border-bottom: 1px solid var(--color-border-light);
}

.sidebar-user-info {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

.sidebar-user-name {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
}

.sidebar-user-email {
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
}

.sidebar.collapsed .sidebar-user-info {
  display: none;
}

.sidebar.collapsed .sidebar-user-header {
  justify-content: center;
  padding-bottom: 0;
  border-bottom: none;
}

.sidebar-user-settings-btn {
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-md);
  cursor: pointer;
  font-size: var(--font-size-lg);
  transition: all var(--transition-base);
  flex-shrink: 0;
}

.sidebar-user-settings-btn:hover {
  background: var(--color-bg-tertiary);
  color: var(--color-text-primary);
  border-color: var(--color-border-medium);
}

.sidebar.collapsed .sidebar-user-settings-btn {
  width: 40px;
  height: 40px;
}

.sidebar-logout-btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-sm) var(--spacing-md);
  background: var(--color-error);
  color: white;
  border: none;
  border-radius: var(--radius-md);
  cursor: pointer;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  transition: all var(--transition-base);
}

.sidebar-logout-icon {
  font-size: var(--font-size-lg);
  line-height: 1;
}

.sidebar-logout-btn:hover {
  background: var(--color-error-dark);
  transform: translateY(-1px);
  box-shadow: var(--shadow-sm);
}

.sidebar-logout-btn:active {
  transform: translateY(0);
}

.sidebar-logout-label {
  text-align: center;
}

.sidebar.collapsed .sidebar-logout-label {
  display: none;
}

.sidebar.collapsed .sidebar-logout-btn {
  padding: var(--spacing-sm);
  min-width: 32px;
}

.change-password-form {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
}

.error-message {
  padding: var(--spacing-sm);
  background: #fee;
  border: 1px solid #fcc;
  border-radius: var(--radius-md);
  color: #c33;
  font-size: var(--font-size-sm);
}

.success-message {
  padding: var(--spacing-sm);
  background: #efe;
  border: 1px solid #cfc;
  border-radius: var(--radius-md);
  color: #3c3;
  font-size: var(--font-size-sm);
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--spacing-md);
  margin-top: var(--spacing-lg);
}

.sidebar-toggle {
  padding: var(--spacing-md);
  background: var(--color-bg-secondary);
  border: none;
  border-top: 1px solid var(--color-border-light);
  cursor: pointer;
  color: var(--color-text-secondary);
  font-size: var(--font-size-lg);
  transition: all var(--transition-base);
}

.sidebar-toggle:hover {
  background: var(--color-bg-tertiary);
  color: var(--color-text-primary);
}

/* Scrollbar styling */
.sidebar-nav::-webkit-scrollbar {
  width: 6px;
}

.sidebar-nav::-webkit-scrollbar-track {
  background: transparent;
}

.sidebar-nav::-webkit-scrollbar-thumb {
  background: var(--color-border-medium);
  border-radius: var(--radius-full);
}

.sidebar-nav::-webkit-scrollbar-thumb:hover {
  background: var(--color-text-secondary);
}
</style>
